import { useCallback, useMemo, useState } from "react";
import { Chart } from "primereact/chart";

import PopupDeviceLocation from "./PopupDeviceLocation";
import { RTU } from "../../types/RTU";
import type { DistributionViewMode } from "../../hooks/usePersistedDistributionView";
import { getLocationCountry } from "./dashboardLocationNormalization";

const ROW_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#C9CBCF",
];

interface LocationCount {
  [key: string]: number;
}

const toPercent = (count: number, total: number) =>
  total > 0 ? Math.round((count / total) * 1000) / 10 : 0;

interface Props {
  rtus: RTU[];
  viewMode: DistributionViewMode;
}

export const DeviceLocationPieChart = ({ rtus, viewMode }: Props) => {
  const [visible, setVisible] = useState(false);
  const [selectedRTUs, setSelectedRTUs] = useState<RTU[]>([]);

  const { rows, topLocationData, total } = useMemo(() => {
    const locationData = rtus.reduce((acc: LocationCount, rtu) => {
      const c = getLocationCountry(rtu.address);
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {} as LocationCount);

    const sortedLocationData = Object.entries(locationData).sort(
      (a, b) => b[1] - a[1]
    );

    const top = sortedLocationData.slice(0, 5);
    const otherCount = sortedLocationData
      .slice(5)
      .reduce((acc, [, count]) => acc + count, 0);

    if (otherCount > 0) {
      top.push(["Other", otherCount]);
    }

    const t = rtus.length;
    const list = top.map(([location, count], index) => ({
      key: location,
      country: location,
      count,
      percent: toPercent(count, t),
      color: ROW_COLORS[Math.min(index, ROW_COLORS.length - 1)]!,
    }));

    return { rows: list, topLocationData: top, total: t };
  }, [rtus]);

  const openForCountry = useCallback(
    (loc: string) => {
      let relatedRTUs: RTU[];

      if (loc === "Other") {
        relatedRTUs = rtus.filter(
          (rtu) =>
            !topLocationData.map(([v]) => v).includes(getLocationCountry(rtu.address))
        );
      } else {
        relatedRTUs = rtus.filter(
          (rtu) => getLocationCountry(rtu.address) === loc
        );
      }

      setSelectedRTUs(relatedRTUs);
      setVisible(true);
    },
    [rtus, topLocationData]
  );

  const closePopup = () => {
    setVisible(false);
  };

  /** Same pattern as historical pie (`type="pie"`, `chartElements` onClick) — data from country buckets. */
  const pieChartData = useMemo(
    () => ({
      labels: topLocationData.map(([location]) => location),
      datasets: [
        {
          data: topLocationData.map(([, count]) => count),
          backgroundColor: [
            "#FF6384",
            "#36A2EB",
            "#FFCE56",
            "#4BC0C0",
            "#9966FF",
            "#C9CBCF",
          ],
        },
      ],
    }),
    [topLocationData]
  );

  const pieChartOptions = useMemo(
    () => ({
      onClick: (event: MouseEvent, chartElements: { index: number }[]) => {
        if (chartElements.length > 0) {
          const index = chartElements[0]!.index;
          const loc = topLocationData[index]?.[0];
          if (loc !== undefined) {
            openForCountry(loc);
          }
        }
      },
    }),
    [topLocationData, openForCountry]
  );

  if (rows.length === 0) {
    return (
      <div
        className="dashboard-distribution-kpi dashboard-distribution-kpi--empty"
        role="status"
      >
        —
      </div>
    );
  }

  return (
    <>
      {viewMode === "pie" ? (
        <div className="dashboard-distribution-pie" id="dashboard-deviceLocation-distribution-pie">
          <Chart
            key="dashboard-deviceLocation-distribution-pie"
            type="pie"
            data={pieChartData}
            options={{ ...pieChartOptions, maintainAspectRatio: false }}
            style={{ cursor: "pointer", width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <div className="dashboard-distribution-kpi" role="list">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              className="dashboard-distribution-kpi__row"
              onClick={() => openForCountry(row.country)}
              aria-label={row.country}
            >
              <div className="dashboard-distribution-kpi__row-top">
                <span
                  className="dashboard-distribution-kpi__label"
                  title={row.country}
                >
                  {row.country}
                </span>
                <span className="dashboard-distribution-kpi__pct">
                  {row.percent}%
                </span>
              </div>
              <div className="dashboard-distribution-kpi__bar-track">
                <span
                  className="dashboard-distribution-kpi__bar"
                  style={{
                    width: `${Math.min(100, row.percent)}%`,
                    background: row.color,
                  }}
                />
              </div>
              <span className="dashboard-distribution-kpi__count">
                {row.count} / {total}
              </span>
            </button>
          ))}
        </div>
      )}
      {visible && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 999,
          }}
        >
          <PopupDeviceLocation rtus={selectedRTUs} onClose={closePopup} />
        </div>
      )}
    </>
  );
};
